import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import {
  TextField, PasswordField, OrDivider, FormError, SubmitButton,
} from '@/components/auth/FormFields';
import { authApi, readApiError } from '@/lib/auth-api';

export function LoginPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const failNote =
    params.get('auth') === 'oauth_google_failed' ? t('auth.error_google_failed')
    : params.get('auth') === 'oauth_apple_failed' ? t('auth.error_apple_failed')
    : params.get('auth') === 'required' ? t('auth.session_expired')
    : null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(failNote);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setPending(true);
    try {
      const res = await authApi.login({ email, password });
      if ('mfaRequired' in res) {
        nav('/mfa-challenge', { replace: true });
        return;
      }
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      nav('/app', { replace: true });
    } catch (e) {
      setErr(readApiError(e, t('auth.error_invalid_credentials')));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout title={t('auth.login_title')} subtitle={t('auth.login_subtitle')}>
      <OAuthButtons />
      <OrDivider label={t('auth.or_email')} />

      <form onSubmit={onSubmit} className="space-y-4">
        <FormError message={err} />
        <TextField
          id="email"
          type="email"
          inputMode="email"
          label={t('auth.email_label')}
          placeholder="cfo@acme.co"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          autoFocus
          required
        />
        <div>
          <PasswordField
            id="password"
            label={t('auth.password_label')}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />
          <Link
            to="/forgot-password"
            className="mt-2 inline-block text-[12.5px] text-emerald-700 hover:underline"
          >
            {t('auth.forgot_password')}
          </Link>
        </div>
        <SubmitButton pending={pending}>{t('auth.sign_in')}</SubmitButton>
      </form>

      <p className="mt-7 text-[13px] text-muted-foreground">
        {t('auth.no_account_yet')}{' '}
        <Link to="/signup" className="font-semibold text-foreground hover:underline">
          {t('auth.sign_up')}
        </Link>
      </p>
    </AuthLayout>
  );
}
